import { Button } from 'antd'
import { useTranslation } from 'react-i18next'
import { OperationModel } from '../../../../../kernel/base-entities'
import { TaskNames, StepTypeNames, Task, Step, StepName, TaskName } from '../../../domain/task'
import Styled from 'styled-components'
import { Can } from '../../../../access-control/infrastructure/roles-permissions/access-control'
import { SubModulePerms } from '../../../../access-control/domain/module'
import { useContext } from 'react'
import { HistoricalContext } from '../historical.context'

const ForceDeliveryButton = Styled(Button)`
  float: right;
  margin-top: 1.2rem;
  margin-bottom: 1.2rem;
  padding-top: 0;
  padding-bottom: 0;
  padding-right: 0;
`
const TaskRestart = Styled.div`
  background-color: #fef5d8;
  border-radius: 0.4rem;
  position: absolute;
  right: 0;
  left: 0;
  padding: 0 3.6rem;
  color: black;
  display: inline-block;
  .force-delivery-text {
    float: left;
    margin-top: 1.8rem;
  }
`

type TaskActionsFooterProps = {
  task: Task
  tasks: Task[]
  forceDelivery: boolean
  operationalModel: OperationModel
  isCancelled: boolean
  isDelivered: () => boolean
  handleConfirmModal: (type: string) => void
}

const TaskActionsFooter = ({
  task,
  tasks,
  forceDelivery,
  operationalModel,
  isCancelled,
  isDelivered,
  handleConfirmModal
}: TaskActionsFooterProps) => {
  const { t } = useTranslation()
  const { historical } = useContext(HistoricalContext)

  const DONE: string = 'DONE'
  const RESET: string = 'RESET'
  const STORAGE: string = 'STORAGE'
  const COLLECT: string = 'COLLECT'

  const isTaskAssigned: boolean = !!task && Object.keys(task.resources).length > 0
  const isAllTasksAssigned: boolean = tasks.every((task) => Object.keys(task.resources).length)

  const hasDeliveringStep = (task: Task): boolean => {
    return task.steps.some((step) => step.type.name === StepTypeNames.DELIVERING)
  }
  const doneStepValidator = (steps: Step[], stepToValidate: StepName): boolean => {
    return steps.some((step) => step.type.name === stepToValidate && step.status === 'DONE')
  }
  const getTaskByName = (tasks: Task[], taskName: TaskName): Task => {
    return tasks.filter((task) => task.name === taskName)[0]
  }
  const wasCheckingOutDone = (tasks: Task[]): boolean => {
    const pickingTask: Task = getTaskByName(tasks, TaskNames.PICKING)
    const fullServiceTask: Task = getTaskByName(tasks, TaskNames.FULL_SERVICE)
    const pickingWithStorage: Task = getTaskByName(tasks, TaskNames.PICKING_WITH_STORAGE)
    const pickingAndStorage: Task = getTaskByName(tasks, TaskNames.PICKING_AND_STORAGE)
    const consolidationTask: Task = getTaskByName(tasks, TaskNames.CONSOLIDATION)
    if (!pickingTask && !fullServiceTask && !pickingWithStorage && !pickingAndStorage && !consolidationTask) {
      return false
    }
    const { steps } = pickingTask || fullServiceTask || pickingWithStorage || pickingAndStorage || consolidationTask
    const checkingOutStep: Step = steps.find((step: Step) => step.type.name === StepTypeNames.CHECKING_OUT)
    return checkingOutStep && checkingOutStep.status !== DONE
  }
  const wasTransferredDone = (tasks: Task[]): boolean => {
    const deliveryTask: Task = getTaskByName(tasks, TaskNames.DELIVERY)
    const deliveryWithStorage: Task = getTaskByName(tasks, TaskNames.DELIVERY_WITH_STORAGE)
    const pickUpForDelivery: Task = getTaskByName(tasks, TaskNames.PICK_UP_FOR_DELIVERY)

    if (!deliveryTask && !deliveryWithStorage && !pickUpForDelivery) return false

    const { steps } = deliveryTask || deliveryWithStorage || pickUpForDelivery
    const transferredStep = steps.find(
      (step: Step) =>
        step.type.name === StepTypeNames.TRANSFERRING_FROM ||
        step.type.name === StepTypeNames.TRANSFERRING_FROM_STORAGE_TO_DELIVERY ||
        step.type.name === StepTypeNames.TRANSFERRING_TO_DELIVERY_FROM_STORAGE ||
        step.type.name === StepTypeNames.TRANSFERRING_FROM_PICKING_TO_STORAGE
    )
    return transferredStep && transferredStep.status !== DONE
  }
  const wasPickupDone = (tasks: Task[]): boolean => {
    const pickUp: Task = getTaskByName(tasks, TaskNames.PICK_UP)
    if (!pickUp) return false
    const { steps } = pickUp
    const deliveredStep = steps.find((step: Step) => step.type.name === StepTypeNames.DELIVERING)
    return deliveredStep && deliveredStep.status !== DONE
  }
  const wasParkingInZonePickingDone = (tasks: Task[]): boolean => {
    const zonePickingTask: Task = getTaskByName(tasks, TaskNames.ZONE_PICKING)
    if (!zonePickingTask) return false
    const { steps } = zonePickingTask
    const parkingStep = steps.find((step: Step) => step.type.name === StepTypeNames.PARKING)
    return parkingStep && parkingStep.status !== DONE
  }

  const canResetTask = ((): boolean => {
    if (task && task.name === TaskNames.CONSOLIDATION) {
      return false
    }
    return (
      Can([SubModulePerms.JOB_TASK_RESTART]) &&
      task &&
      (wasCheckingOutDone([task]) ||
        wasTransferredDone([task]) ||
        wasPickupDone([task]) ||
        wasParkingInZonePickingDone([task]))
    )
  })()

  const canForceDeliveryTask = ((): boolean => {
    if (!historical) return false

    let steps: Step[] = []
    tasks.forEach((task) => {
      task.steps.forEach((step) => {
        steps.push(step)
      })
    })
    let canForceDelivery: boolean = false

    const checkingOutStepDone = doneStepValidator(steps, StepTypeNames.CHECKING_OUT)
    canForceDelivery =
      Can([SubModulePerms.JOB_TASK_FORCE_DELIVERY]) &&
      checkingOutStepDone &&
      forceDelivery &&
      isAllTasksAssigned &&
      task &&
      hasDeliveringStep(task) &&
      !isCancelled &&
      !isDelivered() &&
      !historical.some((h) => h.action === 'makes_delivery')

    if (operationalModel.includes(STORAGE) || operationalModel.includes(COLLECT)) {
      const storingStepDone = doneStepValidator(steps, StepTypeNames.STORING)
      return canForceDelivery && storingStepDone
    }
    return canForceDelivery
  })()

  const shouldRenderFooter = ((): boolean => {
    return isTaskAssigned && (canResetTask || canForceDeliveryTask)
  })()

  const labelText = (() => {
    switch (true) {
      case canResetTask && !canForceDeliveryTask:
        return t('jobs.tasks.reset-text')
      case !canResetTask && canForceDeliveryTask:
        return t('jobs.tasks.force-delivery-txt')
      default:
        return t('jobs.tasks.footer-multi-actions-text')
    }
  })()

  return shouldRenderFooter ? (
    <TaskRestart>
      <div className='force-delivery-text'>{labelText}</div>

      {canResetTask && (
        <ForceDeliveryButton
          id='reset-task'
          type='link'
          onClick={() => handleConfirmModal(RESET)}
          disabled={isCancelled || isDelivered()}
        >
          <u>{t('jobs.tasks.reset-btn')}</u>
        </ForceDeliveryButton>
      )}

      {canForceDeliveryTask && (
        <ForceDeliveryButton id='force-delivery' type='link' onClick={() => handleConfirmModal(TaskNames.DELIVERY)}>
          <u>{t('jobs.tasks.make-delivery')}</u>
        </ForceDeliveryButton>
      )}
    </TaskRestart>
  ) : null
}

export { TaskActionsFooter }
